import * as React from 'react'
import { mtc } from '../../../utils/style-helpers'
import { pillStyles } from './styles'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { Icon } from '../icon'

export type PillColor =
  | 'green'
  | 'olive'
  | 'orange'
  | 'red'
  | 'yellow'
  | 'purple'
  | 'blue'
  | 'pink'
  | 'gray'
  | 'white'

export interface PillProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Set the color of the pill */
  color?: PillColor
  /** Set an optional icon */
  icon?: IconDefinition
  /** Set the size of the pill */
  size?: 'sm' | 'md'
  /** Set the variant of the pill */
  variant?: 'default' | 'secondary' | 'outline' | 'dashed'
  /** Toggle uppercase and tracking-wide styling */
  uppercase?: boolean
  /** Show a dismiss button */
  dismissible?: boolean
  /** Callback when the dismiss button is clicked */
  onDismiss?: () => void
}

export const Pill = React.forwardRef<HTMLDivElement, PillProps>(
  (
    {
      color = 'olive',
      icon,
      className,
      size = 'md',
      variant = 'default',
      uppercase = true,
      dismissible,
      onDismiss,
      children,
      ...props
    },
    ref
  ) => {
    const iconContainerClass = size === 'sm' ? 'text-[10px]' : 'text-xs'
    return (
      <div
        ref={ref}
        className={mtc(
          pillStyles({ color, size, variant, uppercase }),
          className
        )}
        {...props}
      >
        {icon && (
          <span
            className={mtc(
              'inline-flex items-center mr-1.5',
              iconContainerClass
            )}
          >
            <Icon icon={icon} color="text-current" />
          </span>
        )}
        {children}
        {dismissible && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onDismiss?.()
            }}
            className={mtc(
              'ml-1.5 inline-flex items-center focus:outline-none',
              iconContainerClass
            )}
            aria-label="Dismiss"
          >
            <Icon icon={faXmark} color="text-current" />
          </button>
        )}
      </div>
    )
  }
)

Pill.displayName = 'Pill'
